// AutoGatherContext.js
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

const GatherAutoGatherContext = createContext(null);
const ProcessAutoGatherContext = createContext(null);
const CraftAutoGatherContext = createContext(null);

export const AutoGatherProvider = ({ children }) => {
  // GatherTab States
  const [itemCardStates, setItemCardStates] = useState({});
  const [autoGatherStates, setAutoGatherStates] = useState({});
  const [animationStates, setAnimationStates] = useState({});
  const [isGatheringInProgressStates, setIsGatheringInProgressStates] =
    useState({});

  const updateItemCardState = useCallback((itemId, state) => {
    setItemCardStates((prevState) => {
      if (prevState[itemId] === state) {
        return prevState;
      }
      return { ...prevState, [itemId]: state };
    });
  }, []);

  const updateAutoGatherState = useCallback((itemId, state) => {
    setAutoGatherStates((prevState) => {
      if (prevState[itemId] === state) {
        return prevState;
      }
      return { ...prevState, [itemId]: state };
    });
  }, []);

  const updateAnimationState = useCallback((itemId, state) => {
    setAnimationStates((prevState) => {
      if (prevState[itemId] === state) {
        return prevState;
      }
      return { ...prevState, [itemId]: state };
    });
  }, []);

  const updateIsGatheringInProgressState = useCallback((itemId, state) => {
    setIsGatheringInProgressStates((prevState) => {
      if (prevState[itemId] === state) {
        return prevState;
      }
      return { ...prevState, [itemId]: state };
    });
  }, []);

  // ProcessTab states
  const [processingStates, setProcessingStates] = useState({});
  const [processingIntervals, setProcessingIntervals] = useState({});
  const [processingProgress, setProcessingProgress] = useState({});

  const updateProcessingState = useCallback((itemId, state) => {
    setProcessingStates((prevState) => {
      if (prevState[itemId] === state) {
        return prevState;
      }
      return { ...prevState, [itemId]: state };
    });
  }, []);

  const setProcessingInterval = useCallback((itemId, interval) => {
    setProcessingIntervals((prevState) => {
      if (prevState[itemId] === interval) {
        return prevState;
      }
      return { ...prevState, [itemId]: interval };
    });
  }, []);

  const updateProcessingProgress = useCallback((itemId, progress) => {
    setProcessingProgress((prev) =>
      prev[itemId] === progress ? prev : { ...prev, [itemId]: progress }
    );
  }, []);

  const clearProcessingInterval = useCallback((itemId) => {
    setProcessingIntervals((prevState) => {
      const interval = prevState[itemId];
      if (!interval) {
        return prevState;
      }
      clearInterval(interval);
      return { ...prevState, [itemId]: null };
    });
  }, []);

   // CraftTab states
   const [craftingStates, setCraftingStates] = useState({});
   const [craftingIntervals, setCraftingIntervals] = useState({});
   const [craftingProgress, setCraftingProgress] = useState({});
 
   const updateCraftingState = useCallback((itemId, state) => {
     setCraftingStates((prevState) => {
       if (prevState[itemId] === state) {
         return prevState;
       }
       return { ...prevState, [itemId]: state };
     });
   }, []);
 
   const setCraftingInterval = useCallback((itemId, interval) => {
     setCraftingIntervals((prevState) => {
       if (prevState[itemId] === interval) {
         return prevState;
       }
       return { ...prevState, [itemId]: interval };
     });
   }, []);
 
   const updateCraftingProgress = useCallback((itemId, progress) => {
     setCraftingProgress((prev) =>
       prev[itemId] === progress ? prev : { ...prev, [itemId]: progress }
     );
   }, []);
 
   const clearCraftingInterval = useCallback((itemId) => {
     setCraftingIntervals((prevState) => {
       const interval = prevState[itemId];
       if (!interval) {
         return prevState;
       }
       clearInterval(interval);
       return { ...prevState, [itemId]: null };
     });
   }, []);

  const gatherValue = useMemo(
    () => ({
      itemCardStates,
      updateItemCardState,
      autoGatherStates,
      updateAutoGatherState,
      animationStates,
      updateAnimationState,
      isGatheringInProgressStates,
      updateIsGatheringInProgressState,
    }),
    [
      itemCardStates,
      updateItemCardState,
      autoGatherStates,
      updateAutoGatherState,
      animationStates,
      updateAnimationState,
      isGatheringInProgressStates,
      updateIsGatheringInProgressState,
    ]
  );

  const processValue = useMemo(
    () => ({
      processingStates,
      updateProcessingState,
      setProcessingInterval,
      clearProcessingInterval,
      processingProgress,
      updateProcessingProgress,
    }),
    [
      processingStates,
      updateProcessingState,
      setProcessingInterval,
      clearProcessingInterval,
      processingProgress,
      updateProcessingProgress,
    ]
  );

  const craftValue = useMemo(
    () => ({
      craftingStates,
      updateCraftingState,
      setCraftingInterval,
      clearCraftingInterval,
      craftingProgress,
      updateCraftingProgress,
    }),
    [
      craftingStates,
      updateCraftingState,
      setCraftingInterval,
      clearCraftingInterval,
      craftingProgress,
      updateCraftingProgress,
    ]
  );

  return (
    <GatherAutoGatherContext.Provider value={gatherValue}>
      <ProcessAutoGatherContext.Provider value={processValue}>
        <CraftAutoGatherContext.Provider value={craftValue}>
          {children}
        </CraftAutoGatherContext.Provider>
      </ProcessAutoGatherContext.Provider>
    </GatherAutoGatherContext.Provider>
  );
};

export const useGatherAutoGather = () => {
  const ctx = useContext(GatherAutoGatherContext);
  if (!ctx) {
    throw new Error("useGatherAutoGather must be used within AutoGatherProvider");
  }
  return ctx;
};

export const useProcessAutoGather = () => {
  const ctx = useContext(ProcessAutoGatherContext);
  if (!ctx) {
    throw new Error("useProcessAutoGather must be used within AutoGatherProvider");
  }
  return ctx;
};

export const useCraftAutoGather = () => {
  const ctx = useContext(CraftAutoGatherContext);
  if (!ctx) {
    throw new Error("useCraftAutoGather must be used within AutoGatherProvider");
  }
  return ctx;
};

export const useAutoGather = () => {
  return {
    ...useGatherAutoGather(),
    ...useProcessAutoGather(),
    ...useCraftAutoGather(),
  };
};
